import { Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ReactNode, useMemo } from 'react';
import Select from 'components/Common/Select';
import DateRangePicker from 'components/Common/DateRangePicker';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  MdAccessTime,
  MdOutlineAccountTree,
  MdOutlineCall,
  MdOutlineChat,
  MdOutlineChatBubbleOutline,
  MdOutlinePhoneForwarded,
  MdOutlineSms,
  MdOutlineStorage,
  MdOutlineSupportAgent,
  MdOutlineToken
} from 'react-icons/md';
import { useGetBusinessUsageAnalytics, useGetBusinessUsageOverview } from '@/apis/business/setting/revenue/hook';
import { useGetLocations } from '@/apis/business/setting/location/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { useGetBusinessBillingHistory } from '@/apis/business/setting/billing/hook';
import { BillingHistoryResponse } from '@/apis/business/setting/billing/types';
import UsageLoader from './components/Loader';
import UsageMeterCard from './components/UsageMeterCard';

const ALL_LOCATIONS = ' ';
const formatDurationHms = (value?: number | string) => {
  const totalSeconds = Math.max(Math.floor(Number(value) || 0), 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatGb = (value: number) => `${Number(value.toFixed(1)).toLocaleString()} GB`;

export default function UsageAnalytics() {
  const theme = useTheme();

  const { control, watch } = useForm({
    defaultValues: {
      location_id: ALL_LOCATIONS,
      date_range: `${dayjs().subtract(30, 'day').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}`
    }
  });

  const locationId = watch('location_id');
  const dateRange = watch('date_range');
  const selectedLocationId = locationId !== ALL_LOCATIONS ? locationId : undefined;

  const analyticsParams = useMemo(() => {
    const [startDate, endDate] = dateRange?.split(',') ?? [];
    return {
      filter: {
        start_date: startDate,
        end_date: endDate,
        range_type: 'custom_range',
        location_id: selectedLocationId
      }
    };
  }, [dateRange, selectedLocationId]);

  const overviewParams = useMemo(
    () => ({ filter: selectedLocationId ? { location_id: selectedLocationId } : {} }),
    [selectedLocationId]
  );

  const { data: usageAnalyticsData, isLoading: isAnalyticsLoading } = useGetBusinessUsageAnalytics(analyticsParams);
  const { data: overviewData, isLoading: isOverviewLoading } = useGetBusinessUsageOverview(overviewParams);
  const { data: locationsData } = useGetLocations();
  const { data: businessProfile } = useGetBusinessProfile();
  const { data: billingData } = useGetBusinessBillingHistory();

  const workflowRunsIncluded = useMemo(() => {
    const charges: BillingHistoryResponse['addon_charges'] = billingData?.addon_charges ?? [];
    const workflowLine = charges?.find((charge) => charge.code === 'ai_workflow' && charge.type === 'usage_overage');
    return Number(workflowLine?.included_units) || 0;
  }, [billingData?.addon_charges]);

  const locationCount = locationsData?.data?.length ?? 0;
  const isLoading = isAnalyticsLoading || isOverviewLoading;

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 1.5, md: 2 }}
        marginBottom={3}
      >
        <Box>
          <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
            Usage Analytics
          </Typography>
          <Typography variant='body2' color='#777' mt={0.5}>
            View call & chat volume, duration, & AI Agent activity across all locations
          </Typography>
        </Box>
        <Box
          display='flex'
          gap={1.5}
          justifyContent={{ xs: 'stretch', md: 'flex-end' }}
          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
          width={{ xs: '100%', md: 'auto' }}
        >
          <Box width={{ xs: '100%', sm: 220, md: 210 }}>
            <Select
              name='location_id'
              label='Select Locations'
              control={control}
              sortOrder='none'
              options={[
                { value: ALL_LOCATIONS, label: `All Locations (${locationCount})` },
                ...(locationsData?.data?.map((loc) => ({
                  value: loc.id,
                  label: loc.name
                })) || [])
              ]}
              sx={{
                height: { xs: 32, lg: 40 },
                background: theme.palette.background.paper,
                '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' }
              }}
              fullWidth
            />
          </Box>
          <Box width={{ xs: '100%', sm: 260, md: 270 }}>
            <DateRangePicker name='date_range' control={control} createdAt={businessProfile?.created_at} />
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <UsageLoader />
      ) : (
        <>
          <Grid container spacing={3}>
            <AnalyticsCard
              title='Total Agents'
              value={usageAnalyticsData?.total_agents}
              subtitle='Active agents using the platform'
              icon={<MdOutlineSupportAgent />}
            />
            <AnalyticsCard
              title='Total Calls'
              value={usageAnalyticsData?.total_calls}
              subtitle='Total number of calls processed'
              icon={<MdOutlineCall />}
            />
            <AnalyticsCard
              title='Total Messages'
              value={usageAnalyticsData?.total_messages}
              subtitle='Total number of messages processed'
              icon={<MdOutlineChat />}
            />
            <AnalyticsCard
              title='Call Duration'
              value={formatDurationHms(usageAnalyticsData?.total_duration)}
              subtitle='Total call duration'
              icon={<MdAccessTime />}
            />
          </Grid>

          <Typography variant='h2' fontWeight={500} mt={3} mb={0.5}>
            Usage Overview
          </Typography>
          <Typography variant='body2' mb={2} fontSize={16} color='#777'>
            Track usage against your plan&apos;s included limits
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='AI Call Minutes'
                icon={<MdOutlineCall />}
                label='Minutes Used This Cycle'
                used={overviewData?.ai_call_minutes}
                limit={overviewData?.ai_call_minutes_included}
                unit='min'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='AI Warm Call Minutes'
                icon={<MdOutlinePhoneForwarded />}
                label='Warm Call Minutes Used This Cycle'
                used={overviewData?.ai_warm_call_minutes}
                limit={overviewData?.ai_warm_call_minutes_included}
                unit='min'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='AI Chat Messages'
                icon={<MdOutlineChatBubbleOutline />}
                label='Messages Used This Cycle'
                used={overviewData?.ai_chat_messages}
                limit={overviewData?.ai_chat_messages_included}
                unit='messages'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='SMS'
                icon={<MdOutlineSms />}
                label='SMS Used This Cycle'
                used={overviewData?.sms_segments}
                limit={overviewData?.sms_segments_included}
                unit='SMS'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='Workflows'
                icon={<MdOutlineAccountTree />}
                label='Runs Used This Cycle'
                used={overviewData?.workflow_runs}
                limit={workflowRunsIncluded}
                unit='runs'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='AI Tokens'
                icon={<MdOutlineAccountTree />}
                label='AI Tokens Used This Cycle'
                used={overviewData?.ai_tokens}
                limit={overviewData?.ai_tokens_included}
                unit='tokens'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='Storage'
                icon={<MdOutlineStorage />}
                label='Storage Used'
                used={overviewData?.storage_gb}
                limit={overviewData?.storage_gb_included}
                badge='Never resets — cumulative'
                format={formatGb}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UsageMeterCard
                title='AI Tokens'
                icon={<MdOutlineToken />}
                label='Tokens Used This Cycle'
                used={overviewData?.ai_tokens}
                limit={overviewData?.ai_tokens_included}
                unit='tokens'
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

type AnalyticsCardProps = {
  title: string;
  value?: number | string;
  subtitle: string;
  icon: ReactNode;
};

const AnalyticsCard = ({ title, value, subtitle, icon }: AnalyticsCardProps) => {
  const theme = useTheme();
  return (
    <Grid
      size={{ xs: 12, sm: 6, lg: 3 }}
      bgcolor='#fff'
      border='1px solid #ECECEC'
      paddingY={1.5}
      paddingX={{ xs: 2, lg: 2 }}
      borderRadius={2}
      borderLeft={`3px solid ${theme.palette.primary.main}`}
    >
      <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
        <Typography color='secondary' fontWeight={500} sx={{ fontSize: { xs: 13, lg: 14 } }}>
          {title}
        </Typography>
        <Box
          bgcolor={theme.palette.primary.light}
          color={theme.palette.primary.dark}
          borderRadius='50%'
          height='32px'
          width='32px'
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          {icon}
        </Box>
      </Box>
      <Typography variant='h5' mt={1} fontWeight={500} sx={{ fontSize: { xs: '1.4rem', lg: '1.5rem' } }}>
        {value || 0}
      </Typography>
      <Typography
        variant='body1'
        color='#878787'
        fontSize={{ xs: '0.75rem', lg: '0.75rem' }}
        fontWeight={500}
        borderTop='1px solid #ECECEC'
        mt={2}
        pt={1}
        noWrap
      >
        {subtitle}
      </Typography>
    </Grid>
  );
};
